import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {PostbackService} from '../../../../../../services/postback/postback.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {PartnerTabComponent} from '../../../partner-tab/partner-tab.component';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {Partner} from '../../../../../../models/partner/partner';
import {Postback} from '../../../../../../models/postback/postback';
import {forkJoin, Observable} from 'rxjs';
import {GoalService} from '../../../../../../services/goal/goal.service';
import {OfferService} from '../../../../../../services/offer/offer.service';
import {Goal} from '../../../../../../models/goal';
import {Offer} from '../../../../../../models/offer';

import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {InputSwitchComponent} from '../../../../../layout/input-switch/input-switch.component';
import {MetaTitle} from '../../../../../../decorator/meta-title';

@Component({
  selector: 'app-partner-postback-form',
  imports: [
    ReactiveFormsModule,
    InputSwitchComponent,
    FormsModule
],
  templateUrl: './partner-postback-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-postback-form.component.scss'
})
@MetaTitle('Postback form')
export class PartnerPostbackFormComponent implements OnInit {

  protected partner: Partner | null = null;
  protected postback: Postback | null = null;

  protected goals: Goal[] = [];
  protected offers: Offer[] = [];
  protected variables: string[] = [];
  protected selectedOffers: Offer[] = [];
  protected newOffer: Offer | null = null;

  protected form = new FormGroup({
    subId: new FormControl(),
    goalId: new FormControl(),
    name: new FormControl('', Validators.required),
    url: new FormControl('', Validators.required),
    includePrivate: new FormControl(false),
    enabled: new FormControl(true),
  });

  constructor(
    protected loader: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private postbackService: PostbackService,
    private goalService: GoalService,
    private offerService: OfferService,
    private snackbar: SnackbarService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    this.partnerTab.partnerSubject.subscribe(partner => {
      if (!partner) {
        return;
      }

      this.partner = partner;

      const requests: Observable<any>[] = [
        this.goalService.findAll({sort: 'name'}),
        this.offerService.findAll(),
        this.postbackService.fetchVariables(),
      ];

      if (this.route.snapshot.paramMap.has('id')) {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        requests.push(this.postbackService.findOneById(id, {groups: 'offers'}));
      }

      forkJoin(requests).subscribe(([goals, offers, variables, postback]) => {
        this.goals = goals;
        this.offers = offers;
        this.variables = variables;
        this.postback = postback;
        this.selectedOffers = [];

        if (this.postback && this.postback.id) {
          this.form.patchValue({
            subId: this.postback.subId,
            goalId: this.postback.goalId,
            name: this.postback.name,
            url: this.postback.url,
            enabled: this.postback.enabled ?? false,
            includePrivate: this.postback.includePrivate,
          });

          this.postback.offers.forEach((postbackOffer) => {
            const offer = this.offers.find((o) => o.id === postbackOffer.id);
            if (offer) {
              this.selectedOffers.push(offer);
            }
          });
        }

        this.loader.stopLoading();
      });
    });
  }

  insertVariable(variable: string): void {
    let currentUrl = this.form.get('url')?.value || '';

    currentUrl += '{' + variable + '}';

    this.form.patchValue({url: currentUrl});
  }

  submit(): void {
    const postback: Postback | null = this.postback;
    const data: any = this.form.value;

    if (this.partner && this.partner.id) {
      data['partnerId'] = this.partner.id;
    }

    data['offers'] = this.selectedOffers.map(offer => offer.id);

    if (postback && postback.id) {
      this.postbackService.update(postback.id, data).subscribe(() => {
        this.redirectSuccess(`The postback ${data['name']} has been successfully updated.`);
      });
    } else {
      this.postbackService.create(data).subscribe(() => {
        this.redirectSuccess(`The postback ${data['name']} has been successfully created.`);
      });
    }
  }

  private redirectSuccess(message: string): void {
    this.snackbar.success(message);

    if (this.partner && this.partner.id) {
      this.router.navigateByUrl(`/partners/${this.partner.id}/tracking/postback`).then();
    }
  }

  protected addOffer(): void {
    if (this.newOffer) {
      this.selectedOffers.push(this.newOffer);
      this.newOffer = null;
    }
  }

  protected removeOffer(offer: Offer): void {
    const i = this.selectedOffers.findIndex(o => {
      return o.id == offer.id
    });

    if (i >= 0) {
      this.selectedOffers.splice(i, 1);
    }
  }

  getAvailableOffers(): Offer[] {
    return this.offers.filter(offer => {
      return this.selectedOffers.findIndex(o => {
        return o.id == offer.id
      }) == -1;
    });
  }
}
